import React, { useState, useEffect } from 'react';
import { Space, Button, Upload, Input, Select, Divider } from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import ExcelGuideModal from '../../SlotsPage/components/ExcelGuideModal';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import { useAppSelector } from '../../../Modules/Presentation/hooks';
import { isSlotOperationRestricted } from '../../../Utils/timeRestriction';
import { Slot } from '../../../Modules/Slot/Model/Slot';

const { Option } = Select;

interface Props {
  selected: React.Key[];
  selectedSlots: Slot[];
  searchField: 'slot' | 'keyword' | 'url' | 'user' | 'mid_single' | 'mid_compare';
  onSearchFieldChange: (v: Props['searchField']) => void;
  onDelete: () => void;
  search: string;
  onSearchChange: (v: string) => void;
  onSearch: (value: string) => void;
  status: string;
  onStatusChange: (v: string) => void;
  onLegacyExcelDownload: () => void;
  onExcelDownload: () => void;
  excelUploading: boolean;
  legacyExcelDownloading?: boolean;
  excelDownloading?: boolean;
}

const ManagementBar: React.FC<Props> = ({
  selected,
  selectedSlots,
  searchField,
  onSearchFieldChange,
  onDelete,
  search,
  onSearchChange,
  onSearch,
  status,
  onStatusChange,
  onLegacyExcelDownload,
  onExcelDownload,
  excelUploading,
  legacyExcelDownloading = false,
  excelDownloading = false,
}) => {
  const hasSelection = selected.length > 0;
  const [guideOpen, setGuideOpen] = useState(false);
  const currentUser = useAppSelector(selectUser);
  
  // 시간 제한 확인
  const isRestricted = currentUser ? isSlotOperationRestricted(currentUser) : false;

  // 모바일 판별 (<=489px)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 489);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <Space wrap align="center" style={{ padding: isMobile ? 8 : 16, width: '95%' }} size={isMobile ? 'small' : 'middle'}>
      {/* 검색 */}
      <Space size={isMobile ? 'small' : 'middle'} direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
        <Select value={searchField} onChange={onSearchFieldChange} style={{ width: isMobile ? '100%' : 140 }}>
          <Option value="slot">슬롯 번호</Option>
          <Option value="user">아이디</Option>
          <Option value="keyword">키워드</Option>
          <Option value="url">URL</Option>
          <Option value="mid_single">단일 MID</Option>
          <Option value="mid_compare">가격비교 MID</Option>
        </Select>
        {searchField !== 'mid_single' && searchField !== 'mid_compare' && (
          <Input.Search
            placeholder="검색어 입력"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            onSearch={onSearch}
            allowClear
            enterButton="검색"
            style={{ width: isMobile ? '100%' : 250 }}
            size={isMobile ? 'middle' : 'middle'}
          />
        )}
      </Space>

      {/* MID 검색 - MID 필드 선택 시에만 표시 */}
      {(searchField === 'mid_single' || searchField === 'mid_compare') && (
        <Space size={isMobile ? 'small' : 'middle'} direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
          <Input.Search
            placeholder={`${searchField === 'mid_single' ? '단일' : '가격비교'} MID 입력`}
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            onSearch={onSearch}
            allowClear
            enterButton="검색"
            style={{ width: isMobile ? '100%' : 250 }}
            size={isMobile ? 'middle' : 'middle'}
          />
        </Space>
      )}

      {/* 구분선 (모바일에서는 숨김) */}
      {!isMobile && <Divider type="vertical" />}

      {/* 배치 작업 - 비활성 슬롯에서는 모든 사용자가 삭제 가능하지만 시간 제한은 적용 */}
      <Space size={isMobile ? 'small' : 'small'} wrap>
        <Button 
          danger 
          icon={<DeleteOutlined />} 
          onClick={onDelete} 
          disabled={!hasSelection || isRestricted}
          size={isMobile ? 'small' : 'middle'}
        >
          삭제
        </Button>
      </Space>

      {/* 구분선 (모바일에서는 숨김) */}
      {!isMobile && <Divider type="vertical" />}

      {/* 엑셀 */}
      <Space size={isMobile ? 'small' : 'small'} wrap>
        <Button
          icon={<DownloadOutlined />}
          onClick={onLegacyExcelDownload}
          loading={legacyExcelDownloading}
          size={isMobile ? 'small' : 'middle'}
        >
          기존 엑셀 다운로드{selected.length > 0 && ` (${selected.length}개)`}
        </Button>
        <Button
          icon={<DownloadOutlined />}
          onClick={onExcelDownload}
          loading={excelDownloading}
          size={isMobile ? 'small' : 'middle'}
        >
          상품명 엑셀 다운로드{selected.length > 0 && ` (${selected.length}개)`}
        </Button>
      </Space>

      <ExcelGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </Space>
  );
};

export default ManagementBar; 
